import JSZip from 'jszip';
import type { UploadedImage, ProcessingOptions, ProcessingProgress } from '../types';
import { DEFAULT_STANDARD_QUALITY, DEFAULT_TOUR_360_QUALITY, TOUR_360_RATIO_TOLERANCE } from '../constants';

/** True when an image is approximately 2:1 (equirectangular panorama) */
export function isPanoramaRatio(width: number, height: number): boolean {
  if (!width || !height) return false;
  const ratio = width / height;
  return Math.abs(ratio - 2) / 2 <= TOUR_360_RATIO_TOLERANCE;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function getOutputExtension(file: File, force?: 'jpeg' | 'png' | 'webp'): string {
  if (force) return force;
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'png') return 'png';
  if (ext === 'webp') return 'webp';
  return 'jpeg';
}

function getMimeType(ext: string): string {
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  return 'image/jpeg';
}

function padNumber(n: number, totalCount: number): string {
  const digits = Math.max(2, String(totalCount).length);
  return String(n).padStart(digits, '0');
}

interface WatermarkImage {
  element: HTMLImageElement;
  width: number;
  height: number;
}

async function loadWatermark(file: File): Promise<WatermarkImage> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    return { element: img, width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function drawWatermark(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  watermark: WatermarkImage,
  position: string,
  scale: number,
  opacity: number,
  margin: number
): void {
  const wmWidth = canvasWidth * scale;
  const wmHeight = (watermark.height / watermark.width) * wmWidth;

  let x: number;
  let y: number;

  switch (position) {
    case 'top-left':
      x = margin;
      y = margin;
      break;
    case 'top-right':
      x = canvasWidth - wmWidth - margin;
      y = margin;
      break;
    case 'bottom-left':
      x = margin;
      y = canvasHeight - wmHeight - margin;
      break;
    case 'center':
      x = (canvasWidth - wmWidth) / 2;
      y = (canvasHeight - wmHeight) / 2;
      break;
    case 'bottom-right':
    default:
      x = canvasWidth - wmWidth - margin;
      y = canvasHeight - wmHeight - margin;
      break;
  }

  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.drawImage(watermark.element, x, y, wmWidth, wmHeight);
  ctx.restore();
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string, quality: number = 0.92): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to convert canvas to blob'));
      },
      mimeType,
      quality
    );
  });
}

export interface ProcessingResult {
  zipBlob: Blob;
  /** Files that were skipped because they didn't match the required ratio */
  skipped: { fileName: string; reason: string }[];
}

export async function processImages(
  images: UploadedImage[],
  options: ProcessingOptions,
  onProgress: (progress: ProcessingProgress) => void
): Promise<ProcessingResult> {
  const zip = new JSZip();
  let watermark: WatermarkImage | null = null;
  const skipped: { fileName: string; reason: string }[] = [];

  const isTour360 = options.resize.mode === 'tour360';
  const quality =
    options.resize.quality ??
    (isTour360 ? DEFAULT_TOUR_360_QUALITY : DEFAULT_STANDARD_QUALITY);

  if (options.watermark.enabled && options.watermark.logoFile) {
    watermark = await loadWatermark(options.watermark.logoFile);
  }

  for (let i = 0; i < images.length; i++) {
    const image = images[i];
    onProgress({ current: i + 1, total: images.length, currentFileName: image.file.name });

    const img = await loadImage(image.previewUrl);
    const srcW = img.naturalWidth;
    const srcH = img.naturalHeight;

    if (isTour360 && !isPanoramaRatio(srcW, srcH)) {
      skipped.push({
        fileName: image.file.name,
        reason: `Not a 2:1 panorama (got ${srcW}×${srcH})`,
      });
      continue;
    }

    let targetW = srcW;
    let targetH = srcH;

    if (isTour360) {
      // Force exact 2:1 output. Resize by width if a target is set, otherwise keep source width.
      targetW = options.resize.targetWidth && options.resize.targetWidth < srcW
        ? options.resize.targetWidth
        : srcW;
      // Ensure even width for clean 2:1 height
      if (targetW % 2 !== 0) targetW -= 1;
      targetH = targetW / 2;
    } else if (options.resize.targetWidth && options.resize.targetWidth < srcW) {
      targetW = options.resize.targetWidth;
      targetH = Math.round((srcH / srcW) * targetW);
    }

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d')!;

    ctx.drawImage(img, 0, 0, targetW, targetH);

    if (watermark && options.watermark.enabled) {
      drawWatermark(
        ctx,
        targetW,
        targetH,
        watermark,
        options.watermark.position,
        options.watermark.scale,
        options.watermark.opacity,
        options.watermark.margin
      );
    }

    // 360 panoramas always export as JPEG for max compression and tour viewer compatibility.
    const ext = getOutputExtension(image.file, isTour360 ? 'jpeg' : undefined);
    const mime = getMimeType(ext);
    const blob = await canvasToBlob(canvas, mime, quality);

    let fileName: string;
    const outExt = ext === 'jpeg' ? 'jpg' : ext;
    if (options.rename.enabled && options.rename.pattern.trim()) {
      const num = padNumber(i + 1, images.length);
      fileName = `${options.rename.pattern.trim()}-${num}.${outExt}`;
    } else if (isTour360) {
      // Replace the original extension with .jpg so panorama tour software picks it up.
      const base = image.file.name.replace(/\.[^.]+$/, '');
      fileName = `${base}.${outExt}`;
    } else {
      fileName = image.file.name;
    }

    zip.file(fileName, blob);
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  return { zipBlob, skipped };
}

/**
 * Generate a single preview image with watermark applied (for live preview).
 */
export async function generatePreview(
  image: UploadedImage,
  options: ProcessingOptions
): Promise<string> {
  const img = await loadImage(image.previewUrl);
  const srcW = img.naturalWidth;
  const srcH = img.naturalHeight;

  const isTour360 = options.resize.mode === 'tour360';

  let targetW = srcW;
  let targetH = srcH;

  if (isTour360) {
    targetW = options.resize.targetWidth && options.resize.targetWidth < srcW
      ? options.resize.targetWidth
      : srcW;
    if (targetW % 2 !== 0) targetW -= 1;
    targetH = targetW / 2;
  } else if (options.resize.targetWidth && options.resize.targetWidth < srcW) {
    targetW = options.resize.targetWidth;
    targetH = Math.round((srcH / srcW) * targetW);
  }

  const maxPreviewWidth = 800;
  let previewW = targetW;
  let previewH = targetH;
  if (previewW > maxPreviewWidth) {
    previewH = Math.round((previewH / previewW) * maxPreviewWidth);
    previewW = maxPreviewWidth;
  }

  const canvas = document.createElement('canvas');
  canvas.width = previewW;
  canvas.height = previewH;
  const ctx = canvas.getContext('2d')!;

  ctx.drawImage(img, 0, 0, previewW, previewH);

  if (options.watermark.enabled && options.watermark.logoFile) {
    const watermark = await loadWatermark(options.watermark.logoFile);
    drawWatermark(
      ctx,
      previewW,
      previewH,
      watermark,
      options.watermark.position,
      options.watermark.scale,
      options.watermark.opacity,
      options.watermark.margin
    );
  }

  // For 360 mode, reflect the chosen JPEG quality so users can see the visible quality trade-off.
  const previewQuality = isTour360
    ? options.resize.quality ?? DEFAULT_TOUR_360_QUALITY
    : 0.85;
  return canvas.toDataURL('image/jpeg', previewQuality);
}
